import { useState } from 'react'
import { DocsCodeBlock } from './DocsCodeBlock'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { cn } from '@/lib/utils'

// ─── DocAlert ────────────────────────────────────────────────────────────────

interface DocAlertProps {
  variant?: 'warning' | 'note' | 'info' | 'tip'
  children: ReactNode
}

export function DocAlert({ variant = 'note', children }: DocAlertProps) {
  const styles: Record<NonNullable<DocAlertProps['variant']>, string> = {
    warning:
      'border-[var(--color-warning-border)] bg-[var(--color-warning-bg)] text-[var(--color-warning-text)] [&_strong]:text-[var(--color-warning-text)]',
    note: 'border-[var(--color-info-border)] bg-[var(--color-info-bg)] text-[var(--color-info-text)] [&_strong]:text-[var(--color-info-text)]',
    info: 'border-[var(--color-info-border)] bg-[var(--color-info-bg)] text-[var(--color-info-text)] [&_strong]:text-[var(--color-info-text)]',
    tip: 'border-[var(--color-success-border)] bg-[var(--color-success-bg)] text-[var(--color-success-text)] [&_strong]:text-[var(--color-success-text)]',
  }
  const icons: Record<NonNullable<DocAlertProps['variant']>, string> = {
    warning: 'warning',
    note: 'info',
    info: 'info',
    tip: 'lightbulb',
  }

  return (
    <div
      className={cn(
        'flex gap-3 rounded-2xl border p-4 my-4 text-sm leading-relaxed backdrop-blur-md',
        styles[variant],
      )}
    >
      <span className="material-symbols-outlined text-lg shrink-0 mt-0.5">
        {icons[variant]}
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

// ─── DocCard ─────────────────────────────────────────────────────────────────

interface DocCardProps {
  title: string
  description?: string
  icon?: string
  href?: string
  children?: ReactNode
}

export function DocCard({
  title,
  description,
  icon,
  href,
  children,
}: DocCardProps) {
  const content = (
    <div
      className={cn(
        // Utility-based frosting rather than .glass-panel: that class is
        // unlayered CSS, so it would win over the hover border/bg utilities.
        'rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-secondary)]/70 backdrop-blur-md p-5 flex gap-4 transition-all duration-300 ease-brisk h-full',
        href &&
          'hover:border-primary/40 hover:bg-[var(--color-bg-secondary)] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_var(--color-shadow)] cursor-pointer group',
      )}
    >
      {icon && (
        <span className="material-symbols-outlined text-2xl text-primary shrink-0 mt-0.5 transition-transform duration-300 ease-brisk group-hover:scale-110">
          {icon}
        </span>
      )}
      <div className="min-w-0">
        <p className="font-semibold text-[var(--color-text-primary)] mb-1 transition-colors duration-300 ease-brisk group-hover:text-primary">
          {title}
        </p>
        {description && (
          <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
            {description}
          </p>
        )}
        {children && (
          <div className="text-sm text-[var(--color-text-secondary)] leading-relaxed mt-1">
            {children}
          </div>
        )}
      </div>
    </div>
  )

  if (href) {
    const isExternal = href.startsWith('http')
    return isExternal ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="no-underline block h-full"
      >
        {content}
      </a>
    ) : (
      <a href={href} className="no-underline block h-full">
        {content}
      </a>
    )
  }
  return content
}

// ─── DocCardGroup ─────────────────────────────────────────────────────────────

interface DocCardGroupProps {
  cols?: number
  children: ReactNode
}

export function DocCardGroup({ cols = 2, children }: DocCardGroupProps) {
  const colsClass: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  }
  return (
    <div
      className={cn(
        'grid gap-4 my-6',
        colsClass[cols] ?? 'grid-cols-1 sm:grid-cols-2',
      )}
    >
      {children}
    </div>
  )
}

// ─── DocCodeGroup ─────────────────────────────────────────────────────────────

interface DocCodeGroupProps {
  children: ReactNode
}

export function DocCodeGroup({ children }: DocCodeGroupProps) {
  const tabs = extractCodeTabs(children)
  const [active, setActive] = useState(0)
  const [copied, setCopied] = useState(false)

  if (tabs.length === 0) return <div className="my-6">{children}</div>

  const handleCopy = () => {
    const activeTab = tabs[active]
    const codeContent = extractCodeContent(activeTab.content)
    navigator.clipboard.writeText(codeContent)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="relative group my-6 rounded-2xl border border-[var(--color-border)] overflow-hidden">
      <div className="flex items-center gap-1 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-2 py-2">
        <div className="flex flex-1 gap-1">
          {tabs.map((tab, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={cn(
                'rounded-full px-3.5 py-1.5 text-sm font-medium transition-all duration-300 ease-brisk active:scale-[0.97]',
                active === i
                  ? 'bg-primary/10 text-primary ring-1 ring-inset ring-primary/25'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-control-surface-hover)] hover:text-[var(--color-text-primary)]',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          onClick={handleCopy}
          className="rounded-full px-3 py-1.5 text-xs text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-tertiary)] transition-all duration-300 ease-brisk active:scale-[0.97] flex items-center gap-1"
          title={copied ? 'Copied!' : 'Copy code'}
        >
          <span className="material-symbols-outlined text-sm">
            {copied ? 'check' : 'content_copy'}
          </span>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="overflow-x-auto bg-[var(--color-bg-secondary)]">
        {tabs[active]?.content}
      </div>
    </div>
  )
}

function extractCodeTabs(
  children: ReactNode,
): Array<{ label: string; content: ReactNode }> {
  const arr = Array.isArray(children) ? children : [children]
  return arr.filter(Boolean).map((child: any) => {
    // MDX wraps each child in a pre/code block; we get the language from className
    const codeElement = child?.props?.children
    const className = codeElement?.props?.className || ''
    const lang = className.replace('language-', '') || 'code'

    // Wrap code in DocsCodeBlock for syntax highlighting
    const content = (
      <DocsCodeBlock className={className}>
        {codeElement?.props?.children}
      </DocsCodeBlock>
    )

    return { label: lang, content }
  })
}

function extractCodeContent(content: ReactNode): string {
  const child = (content as any)?.props?.children
  return String(child?.props?.children || '')
}

// ─── DocAccordion ─────────────────────────────────────────────────────────────

interface DocAccordionProps {
  title: string
  children: ReactNode
}

export function DocAccordion({ title, children }: DocAccordionProps) {
  return (
    <details className="group border border-[var(--color-border)] rounded-2xl my-2 overflow-hidden transition-colors duration-300 ease-brisk hover:border-primary/30">
      <summary className="flex items-center justify-between gap-4 px-4 py-3 cursor-pointer select-none bg-[var(--color-bg-secondary)] hover:bg-[var(--color-bg-tertiary)] transition-colors duration-300 ease-brisk">
        <span className="font-medium text-[var(--color-text-primary)] text-sm">
          {title}
        </span>
        <span className="material-symbols-outlined shrink-0 text-lg text-[var(--color-text-secondary)] transition-transform duration-300 ease-brisk group-open:rotate-180">
          expand_more
        </span>
      </summary>
      <div className="px-4 py-3 text-sm text-[var(--color-text-secondary)] leading-relaxed border-t border-[var(--color-border)]">
        {children}
      </div>
    </details>
  )
}

// ─── DocAccordionGroup ────────────────────────────────────────────────────────

interface DocAccordionGroupProps {
  children: ReactNode
}

export function DocAccordionGroup({ children }: DocAccordionGroupProps) {
  return <div className="my-4 space-y-1">{children}</div>
}

// ─── DocParamField ────────────────────────────────────────────────────────────

interface DocParamFieldProps {
  name?: string
  type?: string
  required?: boolean
  default?: string
  children: ReactNode
}

export function DocParamField({
  name,
  type,
  required,
  default: defaultVal,
  children,
}: DocParamFieldProps) {
  return (
    <div className="glass-panel rounded-2xl p-4 my-3">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {name && (
          <code className="text-sm font-mono font-semibold text-primary bg-primary/10 px-2.5 py-0.5 rounded-full">
            {name}
          </code>
        )}
        {type && (
          <span className="text-xs text-[var(--color-text-tertiary)] font-mono bg-[var(--color-bg-tertiary)] px-2 py-0.5 rounded-full">
            {type}
          </span>
        )}
        {required && (
          <span className="text-xs font-medium text-[var(--color-warning-text)] border border-[var(--color-warning-border)] bg-[var(--color-warning-bg)] px-2 py-0.5 rounded-full">
            required
          </span>
        )}
        {defaultVal && (
          <span className="text-xs text-[var(--color-text-tertiary)]">
            default: <code className="font-mono">{defaultVal}</code>
          </span>
        )}
      </div>
      <div className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
        {children}
      </div>
    </div>
  )
}

// ─── MdxTable helpers ────────────────────────────────────────────────────────

export function DocTable(props: ComponentPropsWithoutRef<'table'>) {
  return (
    <div className="glass-panel overflow-x-auto rounded-2xl my-6">
      <table className="w-full text-sm border-collapse" {...props} />
    </div>
  )
}

export function DocThead(props: ComponentPropsWithoutRef<'thead'>) {
  return (
    <thead
      className="border-b border-[var(--color-border)] bg-[var(--color-bg-tertiary)]/50"
      {...props}
    />
  )
}

export function DocTh(props: ComponentPropsWithoutRef<'th'>) {
  return (
    <th
      className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-[0.08em] text-[var(--color-text-primary)]"
      {...props}
    />
  )
}

export function DocTd(props: ComponentPropsWithoutRef<'td'>) {
  return (
    <td
      className="px-4 py-3 border-b border-[var(--color-border)] text-[var(--color-text-secondary)]"
      {...props}
    />
  )
}

export function DocTr(props: ComponentPropsWithoutRef<'tr'>) {
  return (
    <tr
      className="transition-colors duration-300 ease-brisk hover:bg-[var(--color-bg-secondary)]"
      {...props}
    />
  )
}
